// list-shape: B (tenant-curated vendor classification default rules, editable and deletable per vendor; docs/design-system/ui-list-pages.md § 11)
/**
 * Tier 2 — Defaults: "what to pre-fill."
 *
 * Vendor classification rules: once a bill's vendor is resolved (by Tier 1 — these
 * rules assert no identity of their own since Phase 5a), the matching rule
 * pre-fills the expense account, tax rate and journal-entry detail level on every
 * future document from that vendor.
 *
 * This is the section's reason to exist: the API has had full CRUD since long
 * before it had a consumer, so these defaults silently shaped every import with no
 * way for a user to see or undo them. Anything that quietly affects future
 * documents has to be visible and reversible.
 *
 * See docs/platform/learned-match-rules-design.md § "Rule taxonomy" and § 5c.
 */

import { useMemo, useState } from 'react';
import { useIsRestoring } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import {
  CardSection,
  DataTable,
  EmptyState,
  LoadingOverlay,
  EntityLoadState,
  ConfirmDialog,
  DialogForm,
  EditIconButton,
  DeleteIconButton,
  BidiText,
  InfoTooltip,
} from '../../../shared/components/common';
import type { Column } from '../../../shared/components/common';
import { LocalizedNumberDisplay, LocalizedDateDisplay } from '../../../shared/components';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useNotification } from '../../../shared/context/NotificationContext';
import { AccountPickerCombobox } from './AccountPickerCombobox';
import { useVendorRules, useUpdateVendorRule, useDeleteVendorRule } from '../hooks/useVendorRules';
import { useTaxRates } from '../hooks/useTaxRates';
import type { VendorClassificationRule, JeDetailLevel } from '../services/accountingApi';
import type { TaxRate } from '../types';

const NO_TAX_RATE = '__none__';
const JE_DETAIL_LEVELS: readonly JeDetailLevel[] = ['summary', 'grouped', 'detailed'];

/**
 * One table row: the defaults for ONE organization, plus every extracted name
 * spelling that resolves to them.
 *
 * The table used to render one row per RULE, keyed on `vendor_name_pattern`,
 * under a column headed "{{Organization}}" — so a vendor whose documents print
 * two spellings of its name appeared twice under a heading promising one row per
 * organization, and there was no way to tell the two rows apart (WI ACC-J1).
 *
 * Rules only merge when EVERY visible cell agrees (same organization AND the same
 * account, tax rate, JE detail and active state), so a merged row is never a
 * summary that hides a difference: two rules for one organization that genuinely
 * disagree stay two rows, which is information the reviewer needs. Every action
 * on the row applies to all of its rules.
 */
interface DefaultsRow {
  key: string;
  rules: VendorClassificationRule[];
  /** The organization these defaults belong to, or null when the rule predates
   *  the Tier-1 linkage and is still matched by text alone. */
  organizationName: string | null;
  /** The extracted spellings that reach these defaults, in list order. */
  patterns: string[];
  representative: VendorClassificationRule;
}

/** Group rules into rows — see {@link DefaultsRow} for why the key is this wide. */
export function groupDefaultsRows(rules: VendorClassificationRule[]): DefaultsRow[] {
  const byKey = new Map<string, DefaultsRow>();
  for (const rule of rules) {
    // An unlinked rule (no organization) can only be identified by its own
    // pattern, so it groups with nothing — including other unlinked rules, whose
    // different spellings may well be different vendors.
    const identity = rule.organization_id ?? `pattern:${rule.id}`;
    const key = [
      identity,
      rule.default_account_id,
      rule.default_tax_rate_id ?? '',
      rule.je_detail_level,
      String(rule.is_active),
    ].join('|');
    const existing = byKey.get(key);
    if (existing) {
      existing.rules.push(rule);
      existing.patterns.push(rule.vendor_name_pattern);
    } else {
      byKey.set(key, {
        key,
        rules: [rule],
        organizationName: rule.organization_name ?? null,
        patterns: [rule.vendor_name_pattern],
        representative: rule,
      });
    }
  }
  return [...byKey.values()];
}

export function VendorDefaultRulesSection() {
  const { t } = useTranslation('accounting');
  const { showSuccess } = useNotification();

  const [editingRule, setEditingRule] = useState<VendorClassificationRule | null>(null);
  const [rowToDelete, setRowToDelete] = useState<DefaultsRow | null>(null);

  // True while the persisted offline cache is being restored — React Query
  // fetches nothing in that window, so every query reads as "not loading, no
  // data" until it ends.
  const isRestoring = useIsRestoring();
  const { data, isLoading, isError, error, refetch } = useVendorRules();
  const rules = useMemo(() => data?.data ?? [], [data]);
  const showLoadState = data === undefined && (isError || isRestoring);
  const rows = useMemo(() => groupDefaultsRows(rules), [rules]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const updateRule = useUpdateVendorRule();
  const deleteRule = useDeleteVendorRule();

  /** The name a row is referred to in copy: its organization, else its spelling. */
  const rowLabel = (row: DefaultsRow): string =>
    row.organizationName ?? row.patterns[0] ?? '';

  const handleToggleActive = async (row: DefaultsRow) => {
    const next = !row.representative.is_active;
    try {
      // Every rule in the row shows the SAME toggle state, so flipping one and
      // leaving its siblings would split the row in two on the next render with
      // no explanation. Bounded by the spellings of one vendor.
      await Promise.all(
        row.rules.map((rule) =>
          updateRule.mutateAsync({ ruleId: rule.id, data: { is_active: next } }),
        ),
      );
      showSuccess(t('documentImport.rules.defaults.updateSuccess'));
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      await Promise.all(rowToDelete.rules.map((rule) => deleteRule.mutateAsync(rule.id)));
      showSuccess(t('documentImport.rules.defaults.deleteSuccess'));
      setRowToDelete(null);
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const columns: Column<DefaultsRow>[] = [
    {
      id: 'organization',
      label: t('documentImport.rules.defaults.columns.vendor'),
      render: (row) =>
        row.organizationName ? (
          <BidiText className="font-medium">{row.organizationName}</BidiText>
        ) : (
          <span className="text-muted-foreground">
            {t('documentImport.rules.defaults.unlinkedOrganization')}
          </span>
        ),
    },
    {
      id: 'patterns',
      label: t('documentImport.rules.defaults.columns.matchedText'),
      headerLabel: (
        <span className="inline-flex items-center gap-1.5">
          {t('documentImport.rules.defaults.columns.matchedText')}
          <InfoTooltip content={t('documentImport.rules.defaults.matchedTextHelp')} />
        </span>
      ),
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.patterns.map((pattern) => (
            <Badge key={pattern} variant="secondary" className="text-xs font-normal">
              <BidiText>{pattern}</BidiText>
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'default_account_id',
      label: t('documentImport.rules.defaults.columns.account'),
      // Resolved by the API, not by this page's own company-scoped account list —
      // that lookup is what printed an em dash for a rule pointing at another
      // company's GL account, reading as "no default" while one was being applied.
      render: (row) =>
        row.representative.default_account_name ? (
          <BidiText>{row.representative.default_account_name}</BidiText>
        ) : (
          <span className="text-muted-foreground">
            {t('documentImport.rules.defaults.unresolvedReference')}
          </span>
        ),
    },
    {
      id: 'default_tax_rate_id',
      label: t('documentImport.rules.defaults.columns.taxRate'),
      // Unlike the account, the tax rate is genuinely optional — so an absent NAME
      // is ambiguous and the two cases must not share a rendering. No id at all is
      // "no tax default", and the dash is right. An id that resolved to nothing is
      // the same silent-pre-fill-reads-as-none defect the account column carried:
      // a rate IS being applied and the cell claims none is.
      render: (row) => {
        const { default_tax_rate_id: id, default_tax_rate_name: name } = row.representative;
        if (name) return <BidiText>{name}</BidiText>;
        return (
          <span className="text-muted-foreground">
            {id ? t('documentImport.rules.defaults.unresolvedReference') : '—'}
          </span>
        );
      },
    },
    {
      id: 'je_detail_level',
      label: t('documentImport.rules.defaults.columns.jeDetail'),
      render: (row) => (
        <span>
          {t(
            `documentImport.rules.defaults.jeDetailLevels.${row.representative.je_detail_level}`,
          )}
        </span>
      ),
    },
    {
      id: 'times_used',
      label: t('documentImport.rules.defaults.columns.timesUsed'),
      align: 'right',
      width: 100,
      render: (row) => (
        <span className="tabular-nums">
          <LocalizedNumberDisplay
            value={row.rules.reduce((sum, rule) => sum + rule.times_used, 0)}
          />
        </span>
      ),
    },
    {
      id: 'last_used_at',
      label: t('documentImport.rules.defaults.columns.lastUsed'),
      width: 140,
      render: (row) => {
        // ISO-8601 sorts lexically, so the last element is the most recent use
        // across the row's merged rules. (Index, not `.at(-1)` — the ship-tree
        // typecheck runs a `lib` older than es2022.)
        const used = row.rules
          .map((rule) => rule.last_used_at)
          .filter((value): value is string => !!value)
          .sort();
        const last = used.length > 0 ? used[used.length - 1] : undefined;
        return last ? (
          <LocalizedDateDisplay date={last} />
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: 'is_active',
      label: t('documentImport.rules.defaults.columns.active'),
      align: 'center',
      width: 80,
      render: (row) => (
        <Switch
          checked={row.representative.is_active}
          onCheckedChange={() => handleToggleActive(row)}
          aria-label={t('documentImport.rules.defaults.toggleActiveAria', {
            vendor: rowLabel(row),
          })}
        />
      ),
    },
  ];

  return (
    <CardSection
      title={t('documentImport.rules.defaults.title')}
      subtitle={t('documentImport.rules.defaults.description')}
    >
      {showLoadState ? (
        <EntityLoadState
          variant="list"
          data={data}
          error={error}
          isLoading={isLoading}
          onRetry={refetch}
        />
      ) : isLoading ? (
        <LoadingOverlay />
      ) : rules.length === 0 ? (
        <EmptyState
          compact
          icon={SlidersHorizontal}
          title={t('documentImport.rules.defaults.empty.title')}
          message={t('documentImport.rules.defaults.empty.message')}
        />
      ) : (
        <DataTable
          columns={columns}
          data={paginatedRows}
          keyField="key"
          page={page}
          pageSize={pageSize}
          totalCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          actionsColumn={(row) => (
            <div className="flex items-center gap-0.5">
              {/* Editing a merged row edits its representative and then applies
                  the result to every rule in it (see the dialog): the row's cells
                  are identical by construction, so one form describes them all. */}
              <EditIconButton
                tooltip={t('common:app.edit')}
                onClick={() => setEditingRule(row.representative)}
              />
              <DeleteIconButton onClick={() => setRowToDelete(row)} />
            </div>
          )}
          actionsColumnWidth={80}
        />
      )}

      {editingRule && (
        <VendorDefaultRuleEditDialog
          rule={editingRule}
          siblingRuleIds={
            rows.find((row) => row.rules.some((rule) => rule.id === editingRule.id))?.rules
              .map((rule) => rule.id) ?? [editingRule.id]
          }
          onClose={() => setEditingRule(null)}
          onSaved={() => {
            setEditingRule(null);
            showSuccess(t('documentImport.rules.defaults.updateSuccess'));
          }}
        />
      )}

      <ConfirmDialog
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title={t('documentImport.rules.defaults.deleteConfirmTitle')}
        message={t('documentImport.rules.defaults.deleteConfirmMessageMulti', {
          vendor: rowToDelete ? rowLabel(rowToDelete) : '',
          count: rowToDelete?.rules.length ?? 0,
        })}
        confirmLabel={t('common:app.delete')}
        onConfirm={handleDelete}
        destructive
      />
    </CardSection>
  );
}

interface VendorDefaultRuleEditDialogProps {
  rule: VendorClassificationRule;
  /**
   * Every rule the edited row stands for — the representative plus the sibling
   * spellings merged into it. The save applies to all of them, because the row
   * only merged rules whose defaults were already identical; writing one and
   * leaving the rest would silently split the row into two contradictory ones.
   */
  siblingRuleIds: string[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit the three defaults the ingestion path actually applies. The vendor match
 * pattern is shown read-only: changing which vendor a rule points at is a Tier-1
 * identity concern, and this is a Tier-2 surface.
 */
function VendorDefaultRuleEditDialog({
  rule,
  siblingRuleIds,
  onClose,
  onSaved,
}: VendorDefaultRuleEditDialogProps) {
  const { t } = useTranslation('accounting');
  const updateRule = useUpdateVendorRule();

  const [accountId, setAccountId] = useState<string>(rule.default_account_id);
  const [taxRateId, setTaxRateId] = useState<string>(rule.default_tax_rate_id ?? NO_TAX_RATE);
  const [jeDetailLevel, setJeDetailLevel] = useState<JeDetailLevel>(rule.je_detail_level);

  const { data: taxRatesData } = useTaxRates({ is_active: true });
  const taxRates: TaxRate[] = taxRatesData?.data ?? [];

  const handleSubmit = async () => {
    const payload = {
      default_account_id: accountId,
      default_tax_rate_id: taxRateId === NO_TAX_RATE ? null : taxRateId,
      je_detail_level: jeDetailLevel,
    };
    try {
      await Promise.all(
        siblingRuleIds.map((ruleId) => updateRule.mutateAsync({ ruleId, data: payload })),
      );
      onSaved();
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DialogForm
      open
      onClose={onClose}
      title={t('documentImport.rules.defaults.editTitle')}
      description={t('documentImport.rules.defaults.editDescription', {
        vendor: rule.vendor_name_pattern,
      })}
      onSubmit={handleSubmit}
      isLoading={updateRule.isPending}
      submitLabel={t('common:app.save')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t('documentImport.rules.defaults.columns.account')}</Label>
          <AccountPickerCombobox
            accountId={accountId}
            onAccountChange={(id) => setAccountId(id)}
            filterTypes={['expense', 'direct_cost']}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('documentImport.rules.defaults.columns.taxRate')}</Label>
          <Select value={taxRateId} onValueChange={setTaxRateId}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TAX_RATE}>
                {t('documentImport.rules.defaults.noTaxRate')}
              </SelectItem>
              {taxRates.map((rate) => (
                <SelectItem key={rate.id} value={rate.id}>
                  {rate.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('documentImport.rules.defaults.columns.jeDetail')}</Label>
          <Select
            value={jeDetailLevel}
            onValueChange={(v) => setJeDetailLevel(v as JeDetailLevel)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {JE_DETAIL_LEVELS.map((level) => (
                <SelectItem key={level} value={level}>
                  {t(`documentImport.rules.defaults.jeDetailLevels.${level}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </DialogForm>
  );
}
